import type { GSelectOption } from '../GSelect';

import { useState, useEffect, useMemo } from 'react';
import { SuggestionsList } from './SuggestionsList';
import { InputActivator } from './InputActivator';
import { InlineStack } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { GSelect } from '../GSelect';

interface GSelectWithAutocompleteProps {
  operatorOptions: GSelectOption[];
  suggestions: string[];
  selectedOperator: string;
  value: string;
  placeholder?: string;
  width?: string;
  onValueChange: (operator: string, value: string) => void;
}

export const GSelectWithAutocomplete = ({
  operatorOptions,
  suggestions,
  selectedOperator,
  value,
  placeholder,
  width,
  onValueChange,
}: GSelectWithAutocompleteProps) => {
  const { t } = useTranslation();

  const [suggestionValue, setSuggestionValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);

  const filteredSuggestions = useMemo(() => {
    if (!suggestionValue.trim()) return suggestions;
    return suggestions.filter((s) => s.toLowerCase().includes(suggestionValue.toLowerCase()));
  }, [suggestionValue, suggestions]);

  const isShowSuggestion = filteredSuggestions.length > 0 && isOpen;

  const handleOperatorSelect = (operator: string) => onValueChange(operator, suggestionValue);

  const handleSuggestionSelect = (suggestion: string) => {
    setSuggestionValue(suggestion);
    onValueChange(selectedOperator, suggestion);
    setIsOpen(false);
  };

  useEffect(() => {
    setSuggestionValue(value);
  }, [value]);

  return (
    <div className="relative w-full">
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <div className="flex-shrink-0">
          <GSelect
            placeholder={t('Select')}
            options={operatorOptions}
            showCheckIcon
            selected={selectedOperator}
            onSelect={handleOperatorSelect}
          />
        </div>
        <div className="w-full">
          <SuggestionsList
            width={width}
            open={isShowSuggestion}
            suggestionValue={suggestionValue}
            activator={
              <InputActivator
                fullWidth
                value={suggestionValue}
                placeholder={placeholder}
                onFocus={() => setIsOpen(true)}
                onChange={(value) => {
                  setSuggestionValue(value);
                  setIsOpen(true);
                }}
                onBlur={() => onValueChange(selectedOperator, suggestionValue)}
                onEnter={handleSuggestionSelect}
              />
            }
            onClose={() => setIsOpen(false)}
            suggestions={filteredSuggestions}
            onSelect={handleSuggestionSelect}
          />
        </div>
      </InlineStack>
    </div>
  );
};
